import {
  differenceInSeconds,
  eachDayOfInterval,
  endOfDay,
  isSameDay,
  isWeekend,
  secondsInDay,
  startOfDay,
} from 'date-fns';
import { keyBy, mapValues } from 'lodash-es';

import { PullRequest } from 'src/types/pull-requests-table';

import { CycleTimeMetricsMap } from './use-cycle-time';

export type StaleMap = {
  [id: string]: boolean;
};

// The minimum number of PRs used to calculate staleness statistics;
// don't show a stale PR flag unless the cycle time statistics are comprised
// of more records than our record count threshold
export const stalePrRecordCountThreshold = 7;

// The minimum threshold that a PR's age must be in order to be considered as "stale"
// Decision: https://hello.atlassian.net/wiki/spaces/IDE/pages/3475718570/Putting+guardrails+in+the+stale+flag+calculations#Decision
export const minPrAgeThresholdInSecs = secondsInDay;

export const useStale = (
  pullRequests: PullRequest[],
  metricsMap: CycleTimeMetricsMap,
  currentDateTime: Date = new Date()
): StaleMap => {
  const prMap = keyBy(pullRequests, pr => pr.id);

  const staleMap = mapValues(prMap, pr => {
    const metrics = metricsMap[pr.destination.repository.uuid];
    const created = new Date(pr.created_on);
    const updated = new Date(pr.updated_on);

    let weekendSeconds = 0;
    const daysBetweenCreatedAndCurrent = eachDayOfInterval({
      start: startOfDay(created),
      end: endOfDay(currentDateTime),
    });

    for (const day of daysBetweenCreatedAndCurrent) {
      if (isWeekend(day) && isSameDay(day, created)) {
        weekendSeconds += differenceInSeconds(endOfDay(created), created);
      } else if (isWeekend(day)) {
        weekendSeconds += secondsInDay;
      }
    }

    const excludeWeekendTimeInSecs = weekendSeconds >= 0 ? weekendSeconds : 0;

    // Only exclude weekend times from the PR age
    const prAgeInSecs = Math.round(
      (currentDateTime.getTime() - created.getTime()) / 1000 -
        excludeWeekendTimeInSecs
    );
    const timeSinceLastUpdateSecs = Math.round(
      (currentDateTime.getTime() - updated.getTime()) / 1000
    );

    const isStale =
      metrics &&
      pr.state === 'OPEN' &&
      metrics.high.value > 0 &&
      prAgeInSecs > minPrAgeThresholdInSecs &&
      prAgeInSecs > metrics.high.value &&
      timeSinceLastUpdateSecs > metrics.mid.value &&
      metrics.recordCount.value > stalePrRecordCountThreshold;

    return isStale;
  });

  return staleMap;
};
